import { useEffect, useState } from "react";
import { storage } from "../utils/storage";
import { Vehicle, MaintenanceRecord, MaintenanceType } from "../types";
import { Wrench, Plus, Edit2, Trash2, X, Calendar, Gauge } from "lucide-react";

const maintenanceTypes: MaintenanceType[] = [
  "Oil Change",
  "Tire Rotation",
  "Brake Service",
  "Air Filter",
  "Battery",
  "Transmission",
  "Inspection",
  "Repair",
  "Other",
];

export function Maintenance() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MaintenanceRecord | null>(null);
  const [filterVehicle, setFilterVehicle] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [vehiclesData, recordsData] = await Promise.all([
        storage.getVehicles(),
        storage.getMaintenanceRecords(),
      ]);
      setVehicles(vehiclesData);
      setRecords(recordsData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formData = new FormData(e.currentTarget);

      const recordData = {
        vehicleId: formData.get("vehicleId") as string,
        type: formData.get("type") as string,
        date: formData.get("date") as string,
        mileage: parseInt(formData.get("mileage") as string),
        cost: parseFloat(formData.get("cost") as string),
        notes: formData.get("notes") as string,
        serviceProvider: formData.get("serviceProvider") as string,
      };

      if (editingRecord) {
        await storage.updateMaintenanceRecord(editingRecord.id, recordData);
      } else {
        const newRecord: MaintenanceRecord = {
          id: crypto.randomUUID(),
          ...recordData,
          createdAt: new Date().toISOString(),
        };
        await storage.addMaintenanceRecord(newRecord);
      }

      await loadData();
      setShowForm(false);
      setEditingRecord(null);
    } catch (error) {
      console.error("Error saving record:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record: MaintenanceRecord) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this maintenance record?")) {
      try {
        await storage.deleteMaintenanceRecord(id);
        await loadData();
      } catch (error) {
        console.error("Error deleting record:", error);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRecord(null);
  };

  const getVehicleName = (vehicleId: string) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    return vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : "Unknown Vehicle";
  };

  const filteredRecords =
    filterVehicle === "all" ? records : records.filter((r) => r.vehicleId === filterVehicle);

  const sortedRecords = [...filteredRecords].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Maintenance Records</h2>
          <p className="text-gray-600 mt-1">Track all service and repairs</p>
        </div>
        {!showForm && vehicles.length > 0 && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Record
          </button>
        )}
      </div>

      {vehicles.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Wrench className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles yet</h3>
          <p className="text-gray-600">Add a vehicle first before tracking maintenance</p>
        </div>
      ) : (
        <>
          {showForm && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingRecord ? "Edit Maintenance Record" : "Add Maintenance Record"}
                </h3>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vehicle *
                    </label>
                    <select
                      name="vehicleId"
                      required
                      defaultValue={editingRecord?.vehicleId}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select a vehicle</option>
                      {vehicles.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maintenance Type *
                    </label>
                    <select
                      name="type"
                      required
                      defaultValue={editingRecord?.type}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select type</option>
                      {maintenanceTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                    <input
                      type="date"
                      name="date"
                      required
                      defaultValue={editingRecord?.date}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mileage *
                    </label>
                    <input
                      type="number"
                      name="mileage"
                      required
                      min="0"
                      defaultValue={editingRecord?.mileage}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="50000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost *</label>
                    <input
                      type="number"
                      name="cost"
                      required
                      min="0"
                      step="0.01"
                      defaultValue={editingRecord?.cost}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="75.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Provider
                    </label>
                    <input
                      type="text"
                      name="serviceProvider"
                      defaultValue={editingRecord?.serviceProvider}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Jiffy Lube"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    name="notes"
                    rows={3}
                    defaultValue={editingRecord?.notes}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Additional details about the service..."
                  />
                </div>
                <div className="flex gap-3 justify-end pt-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? "Saving..." : editingRecord ? "Update Record" : "Add Record"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {!showForm && (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Filter by vehicle:</label>
                <select
                  value={filterVehicle}
                  onChange={(e) => setFilterVehicle(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Vehicles</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {sortedRecords.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <Wrench className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No maintenance records yet
                </h3>
                <p className="text-gray-600 mb-6">Start tracking your vehicle maintenance</p>
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add First Record
                </button>
              </div>
            ) : (
              sortedRecords.map((record) => (
                <div key={record.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{record.type}</h3>
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                          ${record.cost.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{getVehicleName(record.vehicleId)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(record)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(record.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(record.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Gauge className="w-4 h-4" />
                      <span>{record.mileage.toLocaleString()} miles</span>
                    </div>
                    {record.serviceProvider && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Wrench className="w-4 h-4" />
                        <span>{record.serviceProvider}</span>
                      </div>
                    )}
                  </div>
                  {record.notes && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-700">{record.notes}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
